<template>
  <b-modal
    id="build-sequence-modal"
    ref="buildSequenceModal"
    hide-footer
    hide-header
    modal-class="sequence-modal build-sequence-modal"
    centered
    @show="onModalShow"
    @hidden="resetForm"
  >
    <div class="bsm">
      <button
        type="button"
        class="bsm__close"
        aria-label="Close"
        @click="closeModal"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>

      <div class="bsm__header">
        <h2 class="bsm__title">
          <svg
            class="bsm__title-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M10 12h11" />
            <path d="M10 18h11" />
            <path d="M10 6h11" />
            <path d="M4 10h2" />
            <path d="M4 6h1v4" />
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
          </svg>
          Build a Custom Sequence
        </h2>
        <p class="bsm__subtitle">
          Add slots from product images, your files, or by describing what you want.
        </p>
      </div>

      <div class="bsm__body">
        <div class="bsm__name-row">
          <div class="bsm__name-field">
            <label
              class="bsm__label"
              for="sequence-name-input"
            >Sequence name</label>
            <input
              id="sequence-name-input"
              v-model="sequenceName"
              type="text"
              class="bsm__input"
              placeholder="e.g. Frozen Meals Standard"
            >
          </div>
          <button
            type="button"
            class="bsm__clear-btn"
            @click="sequenceName = ''"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line
                x1="10"
                x2="10"
                y1="11"
                y2="17"
              />
              <line
                x1="14"
                x2="14"
                y1="11"
                y2="17"
              />
            </svg>
            Clear
          </button>
        </div>

        <div class="bsm__slots-section">
          <button
            type="button"
            class="bsm__section-toggle"
            @click="slotsExpanded = !slotsExpanded"
          >
            <svg
              class="bsm__chevron"
              :class="{ 'bsm__chevron--collapsed': !slotsExpanded }"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
            <span class="bsm__section-label">Slots ({{ slots.length }})</span>
          </button>

          <div
            v-show="slotsExpanded"
            class="bsm__slots-list"
          >
            <div
              v-if="slots.length === 0"
              class="bsm__slots-empty"
            >
              No slots yet. Add one below.
            </div>
            <draggable
              v-else
              v-model="slots"
              tag="ul"
              class="bsm__slots-ul"
              filter=".bsm__slot-remove, .bsm__slot-source"
              :prevent-on-filter="true"
              :animation="150"
              ghost-class="bsm__slot-row--ghost"
              :force-fallback="true"
              :fallback-on-body="true"
            >
              <li
                v-for="slot in slots"
                :key="slot.id"
                class="bsm__slot-row group"
              >
                <span
                  class="bsm__grip"
                  role="button"
                  tabindex="-1"
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="9"
                      cy="12"
                      r="1"
                    />
                    <circle
                      cx="9"
                      cy="5"
                      r="1"
                    />
                    <circle
                      cx="9"
                      cy="19"
                      r="1"
                    />
                    <circle
                      cx="15"
                      cy="12"
                      r="1"
                    />
                    <circle
                      cx="15"
                      cy="5"
                      r="1"
                    />
                    <circle
                      cx="15"
                      cy="19"
                      r="1"
                    />
                  </svg>
                </span>

                <div class="bsm__slot-main">
                  <img
                    v-if="slot.imageUrl"
                    :src="slot.imageUrl"
                    :alt="slot.label"
                    class="bsm__slot-thumb"
                  >
                  <span class="bsm__slot-label">{{ slot.label }}</span>
                </div>

                <!-- Who produces this image. Presets the matching card mode when the
                     sequence is opened in Creative Studio, where it can still be
                     overridden per card. (POR-14020) -->
                <button
                  type="button"
                  class="bsm__slot-source"
                  :class="slotSource(slot) === 'upload' ? 'bsm__slot-source--upload' : 'bsm__slot-source--ai'"
                  :aria-label="slotSourceTooltip(slot)"
                  :title="slotSourceTooltip(slot)"
                  @click="toggleSlotSource(slot.id)"
                >
                  <svg
                    v-if="slotSource(slot) === 'upload'"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                    />
                    <circle
                      cx="9"
                      cy="9"
                      r="2"
                    />
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                  </svg>
                  <svg
                    v-else
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M9 2 10.6 6.4 15 8l-4.4 1.6L9 14l-1.6-4.4L3 8l4.4-1.6L9 2Z" />
                    <path d="M17.5 12.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6Z" />
                  </svg>
                  {{ slotSource(slot) === 'upload' ? 'Upload' : 'AI' }}
                </button>

                <button
                  type="button"
                  class="bsm__slot-remove"
                  aria-label="Remove slot"
                  @click="removeSlot(slot.id)"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </li>
            </draggable>
          </div>
        </div>

        <div class="bsm__add-type-row">
          <div
            ref="typeDropdown"
            class="bsm__select-wrap"
          >
            <button
              type="button"
              class="bsm__type-trigger"
              :class="{ 'bsm__type-trigger--open': typeDropdownOpen }"
              aria-haspopup="listbox"
              :aria-expanded="typeDropdownOpen ? 'true' : 'false'"
              @click="toggleTypeDropdown"
            >
              + Add image type…
            </button>
            <svg
              class="bsm__select-chevron"
              :class="{ 'bsm__select-chevron--open': typeDropdownOpen }"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
            <ul
              v-show="typeDropdownOpen"
              ref="typeMenu"
              class="bsm__type-menu"
              role="listbox"
            >
              <li
                v-if="!availableImageTypes.length"
                class="bsm__type-empty"
              >
                No more types to add
              </li>
              <li
                v-for="type in availableImageTypes"
                :key="type.key"
                class="bsm__type-option"
                role="option"
                @click="selectImageType(type.key)"
              >
                {{ type.label }}
              </li>
            </ul>
          </div>
          <button
            type="button"
            class="bsm__new-type-btn"
            @click="openDescribeNewType"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
            New type…
          </button>
        </div>

        <div class="bsm__more-ways">
          <button
            type="button"
            class="bsm__section-toggle"
            @click="moreWaysExpanded = !moreWaysExpanded"
          >
            <svg
              class="bsm__chevron"
              :class="{ 'bsm__chevron--collapsed': !moreWaysExpanded }"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
            More ways to add
          </button>

          <div
            v-show="moreWaysExpanded"
            class="bsm__more-ways-content"
          >
            <AdvancedSlotAdding
              ref="advancedSlotAdding"
              :initial-tab="moreWaysTab"
              @add-slot="onAddSlot"
              @remove-slot="onRemoveSlot"
            />
          </div>
        </div>
      </div>

      <div class="bsm__footer">
        <button
          type="button"
          class="bsm__cancel-btn"
          @click="closeModal"
        >
          Cancel
        </button>
        <button
          type="button"
          class="bsm__save-btn"
          :disabled="!canSave || isSaving"
          @click="saveSequence"
        >
          <b-spinner
            v-if="isSaving"
            small
          />
          <svg
            v-else
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
            <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
            <path d="M7 3v4a1 1 0 0 0 1 1h7" />
          </svg>
          Save Sequence
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import AdvancedSlotAdding from '@frontend/components/common/dashboard/modals/AdvancedSlotAdding.vue';
import { loadAllImageTypes } from '@frontend/components/briefs/briefImageTypeUtils';

const DEFAULT_IMAGE_TYPES = [
  { key: 'blog_thumbnail', label: 'Blog Thumbnail' },
  { key: 'display_ad', label: 'Display Ad' },
  { key: 'elevated_hero', label: 'Elevated Hero' },
  { key: 'email_header', label: 'Email Header' },
  { key: 'email_hero', label: 'Email Hero' },
  { key: 'in_store_signage', label: 'In-store Signage' },
  { key: 'landing_page_hero', label: 'Landing Page Hero' },
  { key: 'marketing_banner', label: 'Marketing Banner' },
  { key: 'mobile_hero_a', label: 'Mobile Hero A' },
  { key: 'rtb_image', label: 'RTB Image' },
  { key: 'social_cover', label: 'Social Cover' },
  { key: 'social_post', label: 'Social Post' },
  { key: 'social_story', label: 'Social Story' },
  { key: 'hero', label: 'Hero' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'how_to', label: 'How To' },
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'nutrition_facts', label: 'Nutrition Facts' },
  { key: 'feature_benefits', label: 'Feature-Benefits' },
  { key: 'reason_to_believe', label: 'Reason To Believe' },
];

const DEFAULT_SLOTS = [
  { key: 'elevated_hero', label: 'Elevated Hero' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'how_to', label: 'How To' },
];

function resolveSlotImageUrl(url) {
  if (!url) return null;
  if (
    url.startsWith('blob:')
    || url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('/share?file=')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return url;
  }
  return `/share?file=${url}`;
}

export default {
  name: 'BuildCustomSequenceModal',
  components: {
    AdvancedSlotAdding,
    draggable,
  },
  props: {
    isLibrary: {
      type: Boolean,
      default: false,
    },
    sequence: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      editingSequenceId: null,
      sequenceName: '',
      slotsExpanded: true,
      moreWaysExpanded: false,
      moreWaysTab: 'upload',
      selectedImageType: '',
      typeDropdownOpen: false,
      nextId: 100,
      referenceCounter: 1,
      slots: [],
      imageTypes: DEFAULT_IMAGE_TYPES,
      isSaving: false,
    };
  },
  computed: {
    canSave() {
      return Boolean(this.sequenceName?.trim()) && this.slots.length > 0;
    },
    availableImageTypes() {
      const usedLabels = new Set(this.slots.map((slot) => slot.label.toLowerCase()));
      return this.imageTypes
        .filter((type) => !usedLabels.has(type.label.toLowerCase()))
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onTypeDropdownOutside);
  },
  methods: {
    onModalShow() {
      this.loadImageTypes();
      this.loadFromSequence(this.sequence);
    },
    // Pull the live Image Types catalog (DB-backed, POR-13484) so the sequence
    // builder offers the same types as the Library instead of a static list.
    // Falls back to DEFAULT_IMAGE_TYPES if the catalog can't be loaded.
    //
    // On every open, not once on created(): the Library sits on the next tab of
    // this same page, so a type edited there has to be reflected the next time
    // this modal opens — including the AI/Upload default its slots inherit.
    // (POR-14020)
    async loadImageTypes() {
      try {
        const catalog = await loadAllImageTypes();
        if (Array.isArray(catalog) && catalog.length) {
          this.imageTypes = catalog.map((t) => ({
            key: t.key,
            label: t.name,
            defaultSource: t.default_source === 'upload' ? 'upload' : 'ai',
          }));
        }
      } catch (e) {
        // keep the static fallback list
      }
    },
    loadFromSequence(sequence) {
      if (!sequence) {
        this.editingSequenceId = null;
        this.sequenceName = '';
        this.slots = this.createDefaultSlots();
        return;
      }

      this.editingSequenceId = sequence.id || null;
      this.sequenceName = sequence.name || '';

      const rawSlots = Array.isArray(sequence.slots) ? sequence.slots : [];
      this.slots = rawSlots.map((slot, index) => {
        if (typeof slot === 'string') {
          return this.normalizeSlot({
            id: this.nextId++,
            label: slot,
            type: 'built-in',
          }, index);
        }

        return this.normalizeSlot({
          id: slot.id || this.nextId++,
          key: slot.key,
          label: slot.label || slot.name || `Slot ${index + 1}`,
          type: slot.type,
          source: slot.source,
          imageUrl: slot.image_url || slot.imageUrl || null,
          description: slot.description || null,
        }, index);
      });

      if (!this.slots.length) {
        this.slots = this.createDefaultSlots();
      }
    },
    normalizeSlot(slot) {
      const type = slot.type || 'built-in';
      return {
        id: slot.id || this.nextId++,
        key: slot.key || null,
        label: slot.label,
        type,
        // null = inherit from the Image Type. (POR-14020)
        source: slot.source === 'upload' || slot.source === 'ai' ? slot.source : null,
        imageUrl: resolveSlotImageUrl(slot.imageUrl || slot.image_url),
        description: slot.description || null,
      };
    },
    createDefaultSlots() {
      return DEFAULT_SLOTS.map((slot) => ({
        id: this.nextId++,
        key: slot.key,
        label: slot.label,
        type: 'built-in',
        source: null,
        imageUrl: null,
        description: null,
      }));
    },
    // What a slot of this type produces when the sequence doesn't override it.
    // Unknown keys (custom / described slots) fall back to AI. (POR-14020)
    inheritedSource(slot) {
      const type = this.imageTypes.find((t) => t.key === slot.key);
      return type && type.defaultSource === 'upload' ? 'upload' : 'ai';
    },
    slotSource(slot) {
      return slot.source || this.inheritedSource(slot);
    },
    onImageTypeSelected() {
      if (!this.selectedImageType) return;

      const imageType = this.imageTypes.find((type) => type.key === this.selectedImageType);
      if (!imageType) return;

      this.slots.push({
        id: this.nextId++,
        key: imageType.key,
        label: imageType.label,
        type: 'built-in',
        source: null,
        imageUrl: null,
        description: null,
      });

      this.selectedImageType = '';
    },
    toggleTypeDropdown() {
      this.typeDropdownOpen = !this.typeDropdownOpen;
      if (this.typeDropdownOpen) {
        this.$nextTick(() => {
          this.positionTypeMenu();
          document.addEventListener('mousedown', this.onTypeDropdownOutside);
        });
      } else {
        document.removeEventListener('mousedown', this.onTypeDropdownOutside);
      }
    },
    positionTypeMenu() {
      const wrap = this.$refs.typeDropdown;
      const menu = this.$refs.typeMenu;
      if (!wrap || !menu) return;
      const rect = wrap.getBoundingClientRect();
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${rect.width}px`;
      const menuHeight = menu.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      // Drop down when there's room; otherwise flip above the trigger.
      menu.style.top = spaceBelow >= menuHeight
        ? `${rect.bottom + 4}px`
        : `${Math.max(8, rect.top - menuHeight - 4)}px`;
    },
    onTypeDropdownOutside(event) {
      if (this.$refs.typeDropdown && !this.$refs.typeDropdown.contains(event.target)) {
        this.closeTypeDropdown();
      }
    },
    closeTypeDropdown() {
      this.typeDropdownOpen = false;
      document.removeEventListener('mousedown', this.onTypeDropdownOutside);
    },
    selectImageType(key) {
      this.selectedImageType = key;
      this.onImageTypeSelected();
      this.closeTypeDropdown();
    },
    openDescribeNewType() {
      this.moreWaysExpanded = true;
      this.moreWaysTab = 'describe';
      this.$nextTick(() => {
        this.$refs.advancedSlotAdding?.focusTab('describe');
      });
    },
    removeSlot(slotId) {
      const index = this.slots.findIndex((slot) => slot.id === slotId);
      if (index === -1) return;
      const slot = this.slots[index];
      if (slot.imageUrl && slot.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(slot.imageUrl);
      }
      this.slots.splice(index, 1);
    },
    onAddSlot(slot) {
      const label = slot.label || `Reference ${this.referenceCounter++}`;
      this.slots.push({
        id: this.nextId++,
        key: slot.key || null,
        label,
        type: slot.type || 'custom',
        source: slot.source === 'upload' || slot.source === 'ai' ? slot.source : null,
        imageUrl: slot.imageUrl || null,
        file: slot.file || null,
        description: slot.description || null,
        category: slot.category || null,
        width: slot.width || null,
        height: slot.height || null,
        aiDirection: slot.aiDirection || null,
      });
    },
    // AI ⇄ Upload. Decides whether Creative Studio presets this card to generate
    // the image or to wait for one the user supplies. Landing back on the Image
    // Type's own default stores null instead, so the slot goes back to following
    // the type rather than pinning today's value forever. (POR-14020)
    toggleSlotSource(slotId) {
      const index = this.slots.findIndex((slot) => slot.id === slotId);
      if (index === -1) return;
      const slot = this.slots[index];
      const next = this.slotSource(slot) === 'upload' ? 'ai' : 'upload';
      this.$set(this.slots, index, {
        ...slot,
        source: next === this.inheritedSource(slot) ? null : next,
      });
    },
    slotSourceTooltip(slot) {
      return this.slotSource(slot) === 'upload'
        ? 'You upload this image — click to switch to AI'
        : 'AI generated — click to switch to Upload';
    },
    onRemoveSlot({ imageUrl, label }) {
      const index = this.slots.findIndex((slot) => {
        if (imageUrl && slot.imageUrl === imageUrl) return true;
        return Boolean(label && slot.label === label);
      });
      if (index === -1) return;
      this.removeSlot(this.slots[index].id);
    },
    buildPayload(slots = this.slots) {
      return {
        name: this.sequenceName.trim(),
        slots: slots.map((slot) => ({
          label: slot.label,
          key: slot.key || null,
          type: slot.type === 'built-in' ? 'built-in' : 'custom',
          source: slot.source || null,
          description: slot.description || null,
          image_url: slot.imageUrl && !slot.imageUrl.startsWith('blob:') ? slot.imageUrl : null,
        })),
      };
    },
    async uploadSlotImageFile(file) {
      const formData = new FormData();
      formData.append('upload_images[]', file);
      formData.append('image_type', 'product_image');
      formData.append('company_id', '-1');
      formData.append('max_size', '10000');
      formData.append('resize', '99');
      formData.append('dpi', '75');

      const { data } = await axios.post('/file/uploadimg/store', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const path = data?.paths?.[0];
      if (path) {
        return resolveSlotImageUrl(path);
      }

      throw new Error('Image upload failed');
    },
    async blobUrlToFile(blobUrl, filename) {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type || 'image/png' });
    },
    async prepareSlotsForSave() {
      const prepared = [];
      const blobUrlsToRevoke = [];

      for (const slot of this.slots) {
        let imageUrl = slot.imageUrl;

        if (imageUrl?.startsWith('blob:')) {
          const file = slot.file || await this.blobUrlToFile(
            imageUrl,
            `${(slot.label || 'reference').replace(/\s+/g, '_')}.png`,
          );
          imageUrl = await this.uploadSlotImageFile(file);
          blobUrlsToRevoke.push(slot.imageUrl);
        }

        prepared.push({
          ...slot,
          imageUrl,
          file: null,
        });
      }

      blobUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
      return prepared;
    },
    async saveSequence() {
      if (!this.canSave || this.isSaving) return;

      this.isSaving = true;

      try {
        const preparedSlots = await this.prepareSlotsForSave();
        this.slots = preparedSlots;
        const payload = this.buildPayload(preparedSlots);

        if (this.editingSequenceId) {
          this.$emit('update', {
            sequence: this.editingSequenceId,
            ...payload,
          });
        } else {
          this.$emit('save', payload);
        }

        this.closeModal();
      } catch (error) {
        console.error('Failed to save sequence', error);
        if (typeof toastr !== 'undefined') {
          toastr.error('Failed to upload one or more images. Please try again.');
        }
      } finally {
        this.isSaving = false;
      }
    },
    closeModal() {
      this.$refs.buildSequenceModal.hide();
    },
    resetForm() {
      this.editingSequenceId = null;
      this.sequenceName = '';
      this.slotsExpanded = true;
      this.moreWaysExpanded = false;
      this.moreWaysTab = 'upload';
      this.selectedImageType = '';
      this.referenceCounter = 1;
      this.isSaving = false;
      this.slots.forEach((slot) => {
        if (slot.imageUrl && slot.imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(slot.imageUrl);
        }
      });
      this.slots = this.createDefaultSlots();
    },
  },
};
</script>

<style scoped>
.bsm {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: calc(92vh - 2rem);
  gap: 0;
}

.bsm__close {
  position: absolute;
  top: 0;
  right: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 4px;
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
  z-index: 1;
}

.bsm__close:hover {
  opacity: 1;
  background: #f3f4f6;
}

.bsm__header {
  flex-shrink: 0;
  padding-right: 32px;
}

.bsm__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.bsm__title-icon {
  color: #1aaa8a;
  flex-shrink: 0;
}

.bsm__subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
}

.bsm__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding-right: 4px;
}

.bsm__name-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.bsm__name-field {
  flex: 1;
  min-width: 0;
}

.bsm__label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #0f172a;
}

.bsm__input {
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-size: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.bsm__input:focus {
  outline: none;
  border-color: #1aaa8a;
  box-shadow: 0 0 0 2px rgba(26, 170, 138, 0.15);
}

.bsm__input::placeholder {
  color: #9ca3af;
}

.bsm__clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #ef4444;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}

.bsm__clear-btn:hover {
  background: #fef2f2;
}

.bsm__slots-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bsm__section-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: #6b7280;
  font-size: 12px;
  transition: color 0.15s;
}

.bsm__section-toggle:hover {
  color: #0f172a;
}

.bsm__section-label {
  font-weight: 500;
}

.bsm__chevron {
  transition: transform 0.15s ease;
  flex-shrink: 0;
}

.bsm__chevron--collapsed {
  transform: rotate(-90deg);
}

.bsm__slots-list {
  flex: 1;
  min-height: 120px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}

.bsm__slots-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.bsm__slots-ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.bsm__slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  cursor: grab;
  transition: background 0.12s;
}

.bsm__slot-row:last-child {
  border-bottom: none;
}

.bsm__slot-row:hover {
  background: rgba(0, 0, 0, 0.04);
}

.bsm__slot-row:active {
  cursor: grabbing;
}

.bsm__slot-row--ghost {
  opacity: 0.5;
  background: #edf7f5;
}

.bsm__grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgba(107, 114, 128, 0.4);
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.bsm__grip:active {
  cursor: grabbing;
}

.bsm__slot-row:hover .bsm__grip {
  color: #6b7280;
}

.bsm__slot-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.bsm__slot-thumb {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.bsm__slot-label {
  font-size: 14px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* AI ⇄ Upload pill. Always visible so the whole sequence reads at a glance;
   only the remove button is hover-revealed. (POR-14020) */
.bsm__slot-source {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}

.bsm__slot-source--ai {
  border: 1px solid #a7d7c5;
  background: #eaf6f1;
  color: #2c7a5d;
}

.bsm__slot-source--ai:hover {
  border-color: #7cc4a8;
  background: #ddf0e8;
}

.bsm__slot-source--upload {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.bsm__slot-source--upload:hover {
  border-color: #9ca3af;
  background: #f8fafc;
}

.bsm__slot-source:focus-visible {
  outline: 2px solid #379c75;
  outline-offset: 1px;
}

.bsm__slot-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  opacity: 0;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.12s, color 0.12s, background 0.12s;
}

.bsm__slot-row:hover .bsm__slot-remove {
  opacity: 1;
}

.bsm__slot-remove:hover {
  color: #ef4444;
  background: #fef2f2;
}

.bsm__add-type-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.bsm__select-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.bsm__type-trigger {
  width: 100%;
  height: 36px;
  display: flex;
  align-items: center;
  text-align: left;
  padding: 0 32px 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.bsm__type-trigger:hover {
  border-color: #d1d5db;
}

.bsm__type-trigger--open {
  outline: none;
  border-color: #1aaa8a;
  box-shadow: 0 0 0 2px rgba(26, 170, 138, 0.15);
}

.bsm__type-menu {
  /* Fixed (not absolute) so it escapes the modal's overflow:hidden clip; its
     left/top/width are set in JS from the trigger, with an up-flip when there
     isn't room below. */
  position: fixed;
  z-index: 3000;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  /* Cap the list at ~7 rows (34px each + 4px padding); the rest scrolls. */
  max-height: 256px;
  overflow-y: auto;
}

.bsm__type-option {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 10px;
  font-size: 14px;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bsm__type-option:hover {
  background: #f3f4f6;
}

.bsm__type-empty {
  padding: 10px;
  font-size: 13px;
  color: #9ca3af;
}

.bsm__select-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.5;
  transition: transform 0.15s ease;
}

.bsm__select-chevron--open {
  transform: translateY(-50%) rotate(180deg);
}

.bsm__new-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.bsm__new-type-btn:hover {
  background: #f9fafb;
}

.bsm__more-ways {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bsm__more-ways-content {
  width: 100%;
}

.bsm__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
  margin-top: 16px;
  flex-shrink: 0;
}

.bsm__cancel-btn {
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.bsm__cancel-btn:hover {
  background: #f3f4f6;
}

.bsm__save-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: #189a73;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.bsm__save-btn:hover:not(:disabled) {
  background: #159475;
}

.bsm__save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

::v-deep .build-sequence-modal .modal-dialog {
  max-width: 896px !important;
}

::v-deep .build-sequence-modal .modal-content {
  overflow: hidden;
  border-radius: 8px;
}

::v-deep .build-sequence-modal .modal-body {
  padding: 24px;
}
</style>
